import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithLogout } from "../../app/query";
import {
  CancelUserParkingPermitParameters,
  CancelUserParkingPermitResponse,
  ChangeUserPaymentCardParameters,
  GetChangePaymentCardFormData,
  GetChangePaymentCardFormDataParameters,
  GetUserDetailsParameters,
  GetUserDetailsResponse,
} from "./types";
import {
  GetUserParkingPermitsParameters,
  GetUserParkingPermitsResponse,
  ParkingPermit,
  UpdateUserDetailsParameters,
  UpdateUserDetailsResponse,
  UpdateUserParkingPermitParameters,
  UpdateUserParkingPermitResponse,
} from "./types";

export const userDetailsApi = createApi({
  reducerPath: "userDetailsApi",
  baseQuery: baseQueryWithLogout,
  tagTypes: [
    "GetUserDetailsResponse",
    "GetUserParkingPermitsResponse",
    "GetProductsResponse",
  ],
  refetchOnMountOrArgChange: true,
  endpoints: (builder) => ({
    getUserDetails: builder.query<
      GetUserDetailsResponse,
      GetUserDetailsParameters
    >({
      providesTags: ["GetUserDetailsResponse"],
      query: () => ({
        url: "/v2/me",
        method: "GET",
      }),
    }),
    updateUserDetails: builder.mutation<
      UpdateUserDetailsResponse,
      UpdateUserDetailsParameters
    >({
      invalidatesTags: ["GetUserDetailsResponse"],
      query: (body) => {
        const sanitized = {
          ...body,
          vehicles: body.vehicles.map((vehicle) => ({
            licence_plate: vehicle.licence_plate.trim(),
          })),
        };
        return {
          url: "/v2/me",
          method: "POST",
          body: sanitized,
        };
      },
    }),
    getUserParkingPermits: builder.query<
      GetUserParkingPermitsResponse,
      GetUserParkingPermitsParameters
    >({
      providesTags: ["GetUserParkingPermitsResponse"],
      transformResponse: (response: ParkingPermit[]) =>
        [...response].sort((a, b) => {
          if (a.end_date && b.end_date) {
            // Sort by end_date, fall back to id
            return a.end_date === b.end_date
              ? a.id.localeCompare(b.id)
              : a.end_date.localeCompare(b.end_date);
          }
          return a.start_date.localeCompare(b.start_date);
        }),
      query: () => ({
        url: "/v2/permits",
        method: "GET",
      }),
    }),
    cancelUserParkingPermit: builder.mutation<
      CancelUserParkingPermitResponse,
      CancelUserParkingPermitParameters
    >({
      invalidatesTags: ["GetUserParkingPermitsResponse"],
      query: ({ id, ...body }) => ({
        url: `/v2/permits/${id}/end`,
        method: "POST",
        body,
      }),
    }),
    updateUserParkingPermit: builder.mutation<
      UpdateUserParkingPermitResponse,
      UpdateUserParkingPermitParameters
    >({
      invalidatesTags: ["GetUserParkingPermitsResponse"],
      query: ({ id, ...body }) => ({
        url: `/v2/permits/${id}`,
        method: "POST",
        body,
      }),
    }),
    getChangePaymentCardFormData: builder.mutation<
      GetChangePaymentCardFormData,
      GetChangePaymentCardFormDataParameters
    >({
      query: () => ({
        url: "/v2/change-payment-card",
        method: "POST",
        body: {},
      }),
    }),
    changeUserPaymentCard: builder.mutation<
      undefined,
      ChangeUserPaymentCardParameters
    >({
      invalidatesTags: ["GetUserDetailsResponse"],
      query: (body) => ({
        url: `/v2/change-payment-card/success`,
        method: "POST",
        body,
      }),
    }),
    logUserPaymentCardCancellation: builder.mutation<
      undefined,
      ChangeUserPaymentCardParameters
    >({
      invalidatesTags: ["GetUserDetailsResponse"],
      query: (body) => ({
        url: `/v2/change-payment-card/cancel`,
        method: "POST",
        body,
      }),
    }),
  }),
});

export const {
  useGetUserDetailsQuery,
  useUpdateUserDetailsMutation,
  useGetUserParkingPermitsQuery,
  useUpdateUserParkingPermitMutation,
  useCancelUserParkingPermitMutation,
  useGetChangePaymentCardFormDataMutation,
  useChangeUserPaymentCardMutation,
  useLogUserPaymentCardCancellationMutation,
} = userDetailsApi;
